import React, { JSX, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown, ChevronRight } from 'lucide-react';

import {
  EventRow,
  EventWithDetails,
  type EventRaceSummaryRow,
} from '../types';
import {
  isEventDeletedOnly,
  eventHasAssignableDeviceRaces,
} from '../utils';
import { formatApiDateTime, formatTs, formatUserIdentityDisplay } from '../../../helpers/helpers';
import { EventRacesPanel } from './EventRacesPanel';
import type { EventOverviewEventCardProps } from '../types/EventsOverviewProps';
import EventAssignedOperatorsInline from './EventAssignedOperatorsInline';
import { EventExpandedFileMetadataIcons } from './EventExpandedFileMetadataIcons';
import { CARD_SURFACE, EVENT_CARD_HEADER_SURFACE } from '../../shared/ui';

const EVENT_ACTIONS_MENU_WIDTH_PX = 280;

function placeEventActionsMenu(anchor: HTMLButtonElement): { top: number; left: number } {
  const rect = anchor.getBoundingClientRect();
  let left = rect.right - EVENT_ACTIONS_MENU_WIDTH_PX;
  left = Math.max(8, Math.min(left, window.innerWidth - EVENT_ACTIONS_MENU_WIDTH_PX - 8));
  return { top: rect.bottom + 4, left };
}

function summarizeLinkedWtPrograms(races: EventRaceSummaryRow[]): string {
  const parts: string[] = [];
  const seen = new Set<string>();
  for (const r of races) {
    const pid = String(r.wtProgramId ?? '').trim();
    const pname = String(r.wtProgramName ?? '').trim();
    if (!pid && !pname) continue;
    const key = `${pid}\0${pname}`;
    if (seen.has(key)) continue;
    seen.add(key);
    if (pid && pname) parts.push(`${pid} — ${pname}`);
    else parts.push(pid || pname);
  }
  return parts.join('; ');
}

type MetaItemProps = { label: string; children: React.ReactNode; title?: string };

function MetaItem({ label, children, title }: MetaItemProps): JSX.Element {
  return (
    <div className="min-w-0" title={title}>
      <div className="text-[10px] font-semibold uppercase tracking-wide text-gray-500 dark:text-slate-400">
        {label}
      </div>
      <div className="mt-0.5 text-xs text-gray-800 dark:text-slate-200 break-words">{children}</div>
    </div>
  );
}

export type { EventOverviewEventCardProps };

export function EventOverviewEventCard(props: EventOverviewEventCardProps): JSX.Element {
  const {
    ev,
    details,
    isExpanded,
    isSelected,
    globalUserIdToDisplayName,
    isAdmin,
    isOperator,
    progressingRaceKey,
    onAdvanceRace,
    onSoftDeleteRace,
    onHardDeleteRace,
    onToggleExpanded,
    onOpenAssignOperatorDialog,
    onOpenDevicePackaging,
    onOpenRecallDialog,
    onSoftDeleteEvent,
    onHardDeleteEvent,
    onRemoveOperator,
    onSetDeviceRecalled,
    onRefreshEventDetails,
    isRefreshingEventDetails,
    isDetailsLoaded,
    eventDetailsRefreshVersion,
    onSelectEvent,
    onNavigateToDevices,
  } = props;

  const devices = details?.devices ?? [];
  const races = details?.races ?? [];
  const operators = details?.operators ?? [];
  const eventDeleted = isEventDeletedOnly(ev.status);
  const isRowEditingLocked = eventDeleted;
  const showHistoricalAssignmentsNotice = eventDeleted;
  const lockedHint = 'This event is deleted and cannot be edited.';

  const [actionsOpen, setActionsOpen] = useState(false);
  const [actionsMenuPos, setActionsMenuPos] = useState<{ top: number; left: number } | null>(null);
  const actionsButtonRef = useRef<HTMLButtonElement | null>(null);

  const closeActionsMenu = useCallback(() => {
    setActionsOpen(false);
    setActionsMenuPos(null);
  }, []);

  useLayoutEffect(() => {
    if (!actionsOpen) {
      setActionsMenuPos(null);
      return;
    }
    const btn = actionsButtonRef.current;
    if (!btn) {
      setActionsMenuPos(null);
      return;
    }
    setActionsMenuPos(placeEventActionsMenu(btn));
  }, [actionsOpen]);

  useEffect(() => {
    if (!actionsOpen) return;

    function onDocumentMouseDown(e: MouseEvent): void {
      const target = e.target as HTMLElement | null;
      if (!target) return;
      const root = target.closest('[data-event-actions-root="true"]');
      const menu = target.closest('[data-event-actions-menu="true"]');
      if (!root && !menu) closeActionsMenu();
    }

    function onReposition(): void {
      const btn = actionsButtonRef.current;
      if (!btn) return;
      setActionsMenuPos(placeEventActionsMenu(btn));
    }

    document.addEventListener('mousedown', onDocumentMouseDown);
    window.addEventListener('scroll', onReposition, true);
    window.addEventListener('resize', onReposition);
    return () => {
      document.removeEventListener('mousedown', onDocumentMouseDown);
      window.removeEventListener('scroll', onReposition, true);
      window.removeEventListener('resize', onReposition);
    };
  }, [actionsOpen, closeActionsMenu]);

  useEffect(() => {
    if (!actionsOpen) return;
    function onKeyDown(e: KeyboardEvent): void {
      if (e.key === 'Escape') closeActionsMenu();
    }
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [actionsOpen, closeActionsMenu]);

  const wtProgramsSummary = isDetailsLoaded
    ? summarizeLinkedWtPrograms(races) || '—'
    : isRefreshingEventDetails
      ? '…'
      : '—';

  return (
    <article
      id={`event-row-${ev.eventId}`}
      className={[
        CARD_SURFACE,
        'overflow-hidden transition-shadow',
        isExpanded ? 'ring-1 ring-blue-500/30 shadow-md' : '',
        isSelected ? 'ring-2 ring-blue-500' : '',
      ].join(' ')}
    >
      <div className={EVENT_CARD_HEADER_SURFACE}>
        <div className="flex items-start justify-between gap-3">
          <div className="flex min-w-0 flex-1 items-start gap-3">
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <h2 className="text-base font-semibold text-gray-900 dark:text-slate-100 truncate">
                  {ev.name ?? '(untitled)'}
                </h2>
                <button
                  type="button"
                  className={[
                    'inline-flex items-center gap-1 rounded-md border px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wide transition-all hover:-translate-y-[1px] hover:shadow-sm',
                    isExpanded
                      ? 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800'
                      : 'border-indigo-200 bg-indigo-50 text-indigo-900 hover:bg-indigo-100 dark:border-indigo-700/60 dark:bg-indigo-950/50 dark:text-indigo-100',
                  ].join(' ')}
                  onClick={() => onToggleExpanded(ev.eventId)}
                  title={isExpanded ? 'Hide details' : 'Open event'}
                  aria-label={isExpanded ? 'Hide details' : 'Open event'}
                  aria-expanded={isExpanded}
                >
                  {isExpanded ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
                  {isExpanded ? 'Hide details' : 'Open event'}
                </button>
                {isSelected ? (
                  <span className="inline-flex items-center rounded-full border border-blue-500 bg-blue-600 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-white">
                    Selected
                  </span>
                ) : null}
              </div>
              <p className="mt-1 text-[11px] text-gray-500 dark:text-slate-400">
                {formatTs(ev.startAt)} – {formatTs(ev.endAt)}
                <span className="mx-1.5 text-gray-300 dark:text-slate-600">·</span>
                <span className="font-mono break-all">{ev.eventId}</span>
              </p>
            </div>
          </div>

          {(onSelectEvent ||
            onNavigateToDevices ||
            (isAdmin && (onSoftDeleteEvent || onHardDeleteEvent))) ? (
            <div className="relative shrink-0" data-event-actions-root="true">
              <button
                ref={actionsButtonRef}
                type="button"
                className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-base leading-none text-gray-600 shadow-sm transition-colors hover:bg-gray-50 hover:text-gray-900 dark:hover:bg-slate-800"
                title="Event actions"
                aria-label="Event actions"
                aria-expanded={actionsOpen}
                onClick={(e) => {
                  e.stopPropagation();
                  setActionsOpen((prev) => !prev);
                }}
              >
                &#8230;
              </button>

              {actionsOpen && actionsMenuPos
                ? createPortal(
                    <div
                      data-event-actions-menu="true"
                      className="min-w-[220px] max-w-[min(100vw-2rem,20rem)] max-h-[min(70vh,24rem)] overflow-y-auto overflow-x-hidden rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 py-1 shadow-xl"
                      style={{
                        position: 'fixed',
                        top: actionsMenuPos.top,
                        left: actionsMenuPos.left,
                        width: EVENT_ACTIONS_MENU_WIDTH_PX,
                        zIndex: 4000,
                      }}
                    >
                      {onSelectEvent ? (
                        <button
                          type="button"
                          disabled={isSelected || isRowEditingLocked}
                          title={isRowEditingLocked ? lockedHint : undefined}
                          onClick={() => {
                            if (isRowEditingLocked) return;
                            onSelectEvent(ev.eventId);
                            closeActionsMenu();
                          }}
                          className="w-full px-3 py-2 text-left text-xs text-gray-700 transition-colors hover:bg-gray-50 dark:hover:bg-slate-800 disabled:cursor-not-allowed disabled:bg-gray-50 dark:disabled:bg-slate-800 disabled:text-gray-400 dark:disabled:text-slate-500"
                        >
                          {isSelected ? 'Selected event' : 'Select event'}
                        </button>
                      ) : null}

                      {onNavigateToDevices && eventHasAssignableDeviceRaces(races) ? (
                        <button
                          type="button"
                          disabled={isRowEditingLocked}
                          title={isRowEditingLocked ? lockedHint : undefined}
                          onClick={() => {
                            if (isRowEditingLocked) return;
                            onNavigateToDevices({
                              eventId: ev.eventId,
                              eventName: ev.name ?? null,
                              raceId: null,
                            });
                            closeActionsMenu();
                          }}
                          className="w-full px-3 py-2 text-left text-xs text-gray-700 transition-colors hover:bg-gray-50 dark:hover:bg-slate-800 disabled:cursor-not-allowed disabled:bg-gray-50 dark:disabled:bg-slate-800 disabled:text-gray-400 dark:disabled:text-slate-500"
                        >
                          View All Devices
                        </button>
                      ) : null}

                      {isAdmin && onSoftDeleteEvent ? (
                        <>
                          <div className="my-1 border-t border-slate-200 dark:border-slate-700" />
                          <button
                            type="button"
                            disabled={isRowEditingLocked}
                            title={isRowEditingLocked ? lockedHint : undefined}
                            onClick={() => {
                              if (isRowEditingLocked) return;
                              onSoftDeleteEvent(ev.eventId);
                              closeActionsMenu();
                            }}
                            className="w-full px-3 py-2 text-left text-xs text-red-700 transition-colors hover:bg-red-50 dark:hover:bg-red-950/40 disabled:cursor-not-allowed disabled:opacity-60"
                          >
                            Soft-delete event
                          </button>
                        </>
                      ) : null}

                      {isAdmin && onHardDeleteEvent ? (
                        <button
                          type="button"
                          onClick={() => {
                            onHardDeleteEvent(ev.eventId);
                            closeActionsMenu();
                          }}
                          className="w-full px-3 py-2 text-left text-xs text-red-700 transition-colors hover:bg-red-50 dark:hover:bg-red-950/40"
                        >
                          Hard-delete event
                        </button>
                      ) : null}
                    </div>,
                    document.body,
                  )
                : null}
            </div>
          ) : null}
        </div>

      </div>

      {isExpanded ? (
        <div className="border-t border-slate-200 p-4 overflow-hidden rounded-b-xl dark:border-slate-700">
          <div className="mb-4 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-x-4 gap-y-3">
            <MetaItem label="Start">{formatTs(ev.startAt)}</MetaItem>
            <MetaItem label="End">{formatTs(ev.endAt)}</MetaItem>
            <MetaItem
              label="Created by"
              title={formatUserIdentityDisplay(
                (ev as { createdBy?: unknown }).createdBy,
                globalUserIdToDisplayName,
              )}
            >
              {formatUserIdentityDisplay(
                (ev as { createdBy?: unknown }).createdBy,
                globalUserIdToDisplayName,
              )}
            </MetaItem>
            <MetaItem label="WT event ID">
              {String((ev as { wtEventId?: unknown }).wtEventId ?? '').trim() || '—'}
            </MetaItem>
            <MetaItem
              label="WT event name"
              title={String((ev as { wtEventName?: unknown }).wtEventName ?? '').trim() || undefined}
            >
              {String((ev as { wtEventName?: unknown }).wtEventName ?? '').trim() || '—'}
            </MetaItem>
            <MetaItem label="WT program(s)" title={isDetailsLoaded ? wtProgramsSummary : undefined}>
              {wtProgramsSummary}
            </MetaItem>
            <MetaItem label="Created at">{formatApiDateTime(ev.createdAt, '-')}</MetaItem>
            <MetaItem label="Devices">
              <span className="font-semibold tabular-nums">
                {isDetailsLoaded ? devices.length : isRefreshingEventDetails ? '…' : '—'}
              </span>
            </MetaItem>
            <MetaItem label="Operators">
              <span className="font-semibold tabular-nums">
                {isDetailsLoaded ? operators.length : isRefreshingEventDetails ? '…' : '—'}
              </span>
            </MetaItem>
          </div>
          {showHistoricalAssignmentsNotice ? (
            <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 dark:border-amber-700/60 dark:bg-amber-950/40 px-3 py-2 text-[11px] leading-snug text-amber-900 dark:text-amber-200">
              This event is deleted. Assignments and operators shown here are historical.
            </div>
          ) : null}
          <div className="grid grid-cols-1 gap-4">
            <div className="min-h-0 flex flex-wrap items-start gap-3">
              <div className="min-w-0 flex-1 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 overflow-hidden">
                <EventAssignedOperatorsInline
                  eventId={ev.eventId}
                  operators={operators}
                  isAdmin={isAdmin}
                  isReadOnly={isRowEditingLocked}
                  lockedHint={lockedHint}
                  onOpenAssignOperatorDialog={onOpenAssignOperatorDialog}
                  onRemoveOperator={onRemoveOperator}
                />
              </div>
              <div
                className="shrink-0 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-1 py-1"
                aria-label="Event file metadata"
              >
                <EventExpandedFileMetadataIcons
                  eventId={ev.eventId}
                  globalUserIdToDisplayName={globalUserIdToDisplayName}
                  refreshVersion={eventDetailsRefreshVersion}
                />
              </div>
            </div>
            <div className="min-h-0 min-w-0">
              <EventRacesPanel
                eventId={ev.eventId}
                wtEventId={String((ev as { wtEventId?: unknown }).wtEventId ?? '')}
                eventDeleted={eventDeleted}
                races={races}
                isAdmin={isAdmin}
                isOperator={isOperator}
                canProgressRaces={isAdmin || isOperator}
                onRacesChanged={(raceId) => onRefreshEventDetails?.(ev.eventId, raceId)}
                progressingRaceKey={progressingRaceKey ?? null}
                onAdvanceRace={onAdvanceRace}
                onSoftDeleteRace={onSoftDeleteRace}
                onHardDeleteRace={onHardDeleteRace}
                onNavigateToDevices={
                  onNavigateToDevices
                    ? (raceId, options) =>
                        onNavigateToDevices({
                          eventId: ev.eventId,
                          eventName: ev.name ?? null,
                          raceId: raceId ?? null,
                          unretrievedOnly: options?.unretrievedOnly,
                        })
                    : undefined
                }
                onOpenDevicePackaging={onOpenDevicePackaging}
                onOpenRecall={onOpenRecallDialog}
                devices={devices}
                eventStatus={String(ev.status ?? '')}
                globalUserIdToDisplayName={globalUserIdToDisplayName}
                statusReloadKey={eventDetailsRefreshVersion}
                isDetailsLoaded={isDetailsLoaded}
                isRefreshingEventDetails={isRefreshingEventDetails}
                onSetDeviceRecalled={onSetDeviceRecalled}
              />
            </div>
          </div>
        </div>
      ) : null}
    </article>
  );
}
